import { useTranslation } from "@sagot/i18n";
import { EmptyState, ErrorState, cn, type IconName } from "@sagot/ui";
import type { UseQueryResult } from "@tanstack/react-query";
import { useId, type ReactNode } from "react";

interface ChartCardProps<T> {
  title: ReactNode;
  subtitle?: ReactNode;
  /** Right side of the header (legend, link, note). */
  aside?: ReactNode;
  query: Pick<UseQueryResult<T>, "data" | "isPending" | "isError" | "refetch">;
  isEmpty?: (data: T) => boolean;
  empty?: { icon?: IconName; title?: string; body?: string };
  /** Placeholder shaped like the real content. */
  skeleton: ReactNode;
  className?: string;
  bodyClassName?: string;
  /** Render content without the default `card-body` padding (tables, lists). */
  flush?: boolean;
  testId?: string;
  children: (data: T) => ReactNode;
}

/** Card with header + skeleton / empty / error / content states for one analytics query. */
export function ChartCard<T>({ title, subtitle, aside, query, isEmpty, empty, skeleton, className, bodyClassName, flush, testId, children }: ChartCardProps<T>) {
  const { t } = useTranslation();
  const headingId = useId();
  const data = query.data;
  const body = (() => {
    if (query.isError) return <ErrorState headingLevel={3} title={t("engage.analytics.loadError")} retryLabel={t("common.retry")} onRetry={() => void query.refetch()} />;
    if (query.isPending || data === undefined) return <div aria-hidden="true">{skeleton}</div>;
    if (isEmpty?.(data)) return <EmptyState headingLevel={3} icon={empty?.icon ?? "bar-chart"} title={empty?.title ?? t("engage.analytics.emptyCardTitle")} description={empty?.body} />;
    return children(data);
  })();
  return (
    <section className={cn("card min-w-0", flush && "overflow-hidden", className)} aria-labelledby={headingId} aria-busy={query.isPending} data-testid={testId}>
      <div className="card-header">
        <div className="min-w-0">
          <h2 id={headingId} className="card-title flex items-center gap-2">
            {title}
          </h2>
          {subtitle ? <div className="mt-0.5 text-xs text-fg-subtle">{subtitle}</div> : null}
        </div>
        {aside}
      </div>
      {flush ? body : <div className={cn("card-body", bodyClassName)}>{body}</div>}
    </section>
  );
}
